import { useEffect, useState } from 'react';
import PubSub from 'pubsub-js';
import { useNavigate } from 'react-router-dom';
import LeadsService from '../../Services/LeadsService';
import ContentLoader from 'react-content-loader';
import Dropdown from 'react-bootstrap/Dropdown';
import moment from 'moment';

function AdminListLeads({ onBack }) {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [usedByOptions, setUsedByOptions] = useState([]);
    const [selectedUsedBy, setSelectedUsedBy] = useState('');
    const [emailFilter, setEmailFilter] = useState('');
    const [debouncedEmail, setDebouncedEmail] = useState('');
    const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
    const [hasEmailOnly, setHasEmailOnly] = useState(false);
    const [lastAction, setLastAction] = useState({ index: 0, checked: false });
    const [shiftKeyPressed, setShiftKeyPressed] = useState(false);
    const limit = 100;

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedEmail(emailFilter), 500);
        return () => clearTimeout(handler);
    }, [emailFilter]);

    useEffect(() => {
        if (emailFilter !== debouncedEmail) return;
        fetchLeads();
    }, [page, selectedUsedBy, debouncedEmail, showUnassignedOnly, hasEmailOnly]);

    useEffect(() => {
        loadUsedByOptions();
    }, []);

    useEffect(() => {
        function down(e) { if (e.key === 'Shift') setShiftKeyPressed(true); }
        function up(e) { if (e.key === 'Shift') setShiftKeyPressed(false); }
        document.addEventListener('keydown', down);
        document.addEventListener('keyup', up);
        return () => {
            document.removeEventListener('keydown', down);
            document.removeEventListener('keyup', up);
        };
    }, []);

    async function fetchLeads() {
        setLoading(true);
        let { data, total } = await LeadsService.getLeads(page, limit, selectedUsedBy || null, debouncedEmail || null, showUnassignedOnly, hasEmailOnly);
        setLeads(data || []);
        setTotal(total || 0);
        setLoading(false);
    }

    async function loadUsedByOptions() {
        let options = await LeadsService.getUsedByOptions();
        setUsedByOptions(options || []);
    }

    function onCheckLead(event, index) {
        let checked = event.target.checked;
        leads[index].checked = checked;

        if (shiftKeyPressed) {
            let start = Math.min(lastAction.index, index);
            let end = Math.max(lastAction.index, index);
            for (let i = start; i <= end; i++) {
                leads[i].checked = checked;
            }
        }

        setLastAction({ index, checked });
        setLeads([...leads]);
    }

    async function deleteSelectedLeads() {
        let leads_id = leads.filter(l => l.checked).map(l => l._id);
        if (leads_id.length === 0) return;
        if (window.confirm(`Are you sure you want to delete (${selectedCount}) leads?`)) {
            let { message } = await LeadsService.deleteLeads(leads_id);
            setLeads(leads.filter(l => !leads_id.includes(l._id)));
            fetchLeads();
            PubSub.publish('show-basic-notification', { title: 'Leads deleted', message });
        }
    }

    const pages = Math.ceil(total / limit) || 1;

    const handleBack = onBack || (() => navigate('/admin/leads'));

    const renderPaginationOptions = () => {
        let opts = [];
        for (let i = 0; i < pages; i++) {
            opts.push(<option key={i} value={i + 1}>{i + 1}</option>);
        }
        return opts;
    };

    const renderSkeleton = () => {
        let rows = [];
        for (let i = 0; i < 10; i++) {
            rows.push(
                <tr key={i}>
                    <td colSpan="10">
                        <ContentLoader
                            speed={2}
                            height={20}
                            width={1400}
                            viewBox="0 0 1400 20"
                            backgroundColor="#f3f3f3"
                            foregroundColor="#ecebeb"
                        >
                            <rect x="0" y="0" rx="3" ry="3" width="1400" height="20" />
                        </ContentLoader>
                    </td>
                </tr>
            );
        }
        return rows;
    };

    const start = 1 + (page - 1) * limit;
    const end = Math.min(page * limit, total);
    const selectedCount = leads.filter(l => l.checked).length;
    const leadsToRender = leads;

    return (
        <div className="ulysses-search-container">
            <div className="ulysses-search-content">
                <button onClick={handleBack} className="btn btn-link px-0 mb-3">
                    <i className="fas fa-chevron-left me-2"></i>
                    Back
                </button>
                <h5 className="my-3 ulysses-search-title d-flex align-items-center justify-content-between">
                    <div>
                        <b>Leads <span className="ms-1">({total})</span></b>
                        <button onClick={() => fetchLeads()} className="btn btn-link p-0 ms-2">
                            <i className="fas fa-sync"></i>
                        </button>
                    </div>

                    <button
                        onClick={deleteSelectedLeads}
                        disabled={selectedCount === 0}
                        className="btn btn-outline-danger btn-sm ms-2">
                        <i className="fas fa-trash me-1"></i>
                        Delete {selectedCount > 0 ? `(${selectedCount})` : ''} leads
                    </button>
                </h5>
                <div className="mb-3" style={{ maxWidth: 200 }}>
                    <select
                        value={selectedUsedBy}
                        onChange={e => { setSelectedUsedBy(e.target.value); setPage(1); }}
                        className="form-select form-select-sm">
                        <option value="">All users</option>
                        {usedByOptions.map(opt => (
                            <option key={opt._id} value={opt._id}>{opt.email}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-3" style={{ maxWidth: 250 }}>
                    <input
                        type="text"
                        value={emailFilter}
                        onChange={e => { setEmailFilter(e.target.value); setPage(1); }}
                        placeholder="Filter by email"
                        className="form-control form-control-sm" />
                </div>
                <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="unassigned-filter"
                        checked={showUnassignedOnly}
                        onChange={e => { setShowUnassignedOnly(e.target.checked); setPage(1); }} />
                    <label className="form-check-label" htmlFor="unassigned-filter">
                        Show unassigned only
                    </label>
                </div>
                <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="has-email-filter"
                        checked={hasEmailOnly}
                        onChange={e => { setHasEmailOnly(e.target.checked); setPage(1); }} />
                    <label className="form-check-label" htmlFor="has-email-filter">
                        Show leads with email only
                    </label>
                </div>
                <div className="ulysses-search-table" style={{ overflowX: 'auto' }}>
                    <table className="table table-bordered small" style={{ minWidth: '1400px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 30 }}>
                                    <input type="checkbox"
                                        checked={leadsToRender.length > 0 && selectedCount === leadsToRender.length}
                                        onChange={e => { leadsToRender.forEach(l => l.checked = e.target.checked); setLeads([...leads]); }} />
                                </th>
                                <th style={{ minWidth: 200 }}>Name</th>
                                <th>Title</th>
                                <th style={{ minWidth: 200 }}>Company</th>
                                <th style={{ minWidth: 200 }}>Industry</th>
                                <th>Location</th>
                                <th style={{ minWidth: 150 }}># Employees</th>
                                <th style={{ minWidth: 200 }}>Lead Email</th>
                                <th style={{ minWidth: 200 }}>Assigned To</th>
                                <th style={{ minWidth: 160 }}>Created At</th>
                                <th style={{ minWidth: 160 }}>Used At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                renderSkeleton()
                            ) : (
                                leadsToRender.map((lead, i) => (
                                    <tr key={i}>
                                        <td>
                                            <input type="checkbox" className="form-check-input"
                                                checked={!!lead.checked}
                                                onChange={e => onCheckLead(e, leads.indexOf(lead))} />
                                        </td>
                                        <td>{lead.name}</td>
                                        <td>{lead.title}</td>
                                        <td>{lead.organization_name}</td>
                                        <td>{lead.industry}</td>
                                        <td>{lead.city}</td>
                                        <td>{lead.num_employees}</td>
                                        <td>{lead.email}</td>
                                        <td>
                                            {lead.used_by ? (
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="muted" className="no-chevron btn-sm" id={`lead-dropdown-${i}`}>
                                                        {lead.used_by.email || lead.used_by.name}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item as="a" href={`/admin/users/detail/${lead.used_by._id}`} target="_blank" rel="noreferrer">View user detail</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            ) : 'Unassigned'}
                                        </td>
                                        <td>{lead.created_at ? moment(lead.created_at).format('ll LTS') : ''}</td>
                                        <td>{lead.used_at ? moment(lead.used_at).format('ll LTS') : ''}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="ulysses-search-pagination mt-2">
                    <div>{start} - {end} of {total} leads</div>
                    <button onClick={() => setPage(Math.max(page - 1, 1))} disabled={page === 1} className="btn btn-secondary btn-sm ms-3">
                        <i className="fas fa-chevron-left"></i>
                    </button>
                    <select value={page} onChange={e => setPage(parseInt(e.target.value))} className="btn btn-secondary">
                        {renderPaginationOptions()}
                    </select>
                    <button onClick={() => setPage(Math.min(page + 1, pages))} disabled={page === pages} className="btn btn-secondary btn-sm">
                        <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminListLeads;
